import localizer from 'share/localization/Izenda.Localizer';
import { getOr, pick, isFunction } from 'lodash/fp';

export const XAXIS_LABEL_KEY = 'REPORTDESIGNER_LABEL_LABELS';
export const XAXIS_VALUE_KEY = 'REPORTDESIGNER_LABEL_VALUES';
export const YAXIS_LABEL_KEY = 'REPORTDESIGNER_LABEL_LABELS';
export const YAXIS_VALUE_KEY = 'REPORTDESIGNER_LABEL_VALUES';

const REPORTDESIGNER_LABEL_X_AXIS_NAME_KEY = 'REPORTDESIGNER_LABEL_X_AXIS_NAME';
const REPORTDESIGNER_LABEL_Y_AXIS_NAME_KEY = 'REPORTDESIGNER_LABEL_Y_AXIS_NAME';

const USE_SEPARATOR_PROP_PATH = 'properties.optionByType.izUseSeparator';
const DEFAULT_CONTAINER_HEIGHT = 70;
/**
 * Create a general label input container
 * @param {string} containerKey key
 * @param {string|Function} label The label text value or a function return a text
 * @param {string} dataKey Data key
 * @param {int} height Height of container
 * @param {int} maximumField Maximum number of fields in the container
 * @param {boolean} isHorizontal Is horizontal alignment between label and input
 */
export const createContainerSchema = (
  containerKey,
  label,
  dataKey,
  height,
  maximumField,
  isHorizontal = false
) => () => ({
  container: {
    containerKey: containerKey,
    get label() {
      return isFunction(label) ? label() : label;
    },
    dataKey,
    height: height || DEFAULT_CONTAINER_HEIGHT,
    maximumField,
    isHorizontal
  }
});

/**
 * Create a conditional general label input container
 * @param {Function} predicateFn A predicate to determine whether render the container or not
 * @param {string} containerKey key
 * @param {string|Function} label The label text value or a function return a text
 * @param {string} dataKey Data key
 * @param {int} height Height of container
 * @param {int} maximumField Maximum number of fields in the container
 * @param {boolean} [isHorizontal=false] Is horizontal alignment between label and input
 */
export const createConditionalContainerSchema = (
  predicateFn,
  containerKey,
  label,
  dataKey,
  height,
  maximumField,
  isHorizontal = false
) => reportPartContent =>
  predicateFn(reportPartContent)
    ? {
        container: {
          containerKey: containerKey,
          get label() {
            return isFunction(label) ? label() : label;
          },
          dataKey,
          height,
          maximumField,
          isHorizontal
        }
      }
    : null;

/**
 * Create a X-Axis container with custom label.
 * @param {int} maximumField Maximum number of fields in the container
 * @param {string} xAxisName Localized x-axis name.
 * @param {boolean} isLocalizationNeeded Whether we need to pass througth the localization or not.
 */
export const createXAxisCustomNameSchema = (maximumField, xAxisName, isLocalizationNeeded = false) =>
  () => ({
    chart_xaxis: {
      maximumField: maximumField,
      axisCustomName: isLocalizationNeeded ? localizer.getText(xAxisName) : xAxisName
    }
  });

/**
 * Create a X-Axis container
 * @param {int} maximumField Maximum number of fields in the container
 * @param {string} xAxisNameKey The locale key of xAxis name. Default value is 'Label'.
 */
export const createXAxisSchema = (maximumField, xAxisNameKey = XAXIS_LABEL_KEY) =>
  createXAxisCustomNameSchema(
    maximumField,
    String.format(localizer.getText(REPORTDESIGNER_LABEL_X_AXIS_NAME_KEY), localizer.getText(xAxisNameKey)));

/**
 * Create a X-Axis container for transposed chart (labeled as "Y-axis")
 * @param {int} maximumField Maximum number of fields in the container
 * @param {string} xAxisNameKey The locale key of xAxis name. Default value is 'Label'.
 */
export const createXAxisSchemaWithTransposedLabel = (maximumField, xAxisNameKey = YAXIS_LABEL_KEY) => 
  createXAxisCustomNameSchema(
    maximumField,
    String.format(localizer.getText(REPORTDESIGNER_LABEL_Y_AXIS_NAME_KEY), localizer.getText(xAxisNameKey)));

/**
 * Create a Y-Axis container with custom label.
 * @param {int} maximumField Maximum number of fields in the container
 * @param {string} yAxisName Localized y-axis name.
 * @param {boolean} isLocalizationNeeded Whether we need to pass througth the localization or not.
 */
export const createYAxisCustomNameSchema = (maximumField, yAxisName, isLocalizationNeeded = false) =>
  () => ({
    chart_yaxis: {
      maximumField: maximumField,
      axisCustomName: isLocalizationNeeded ? localizer.getText(yAxisName) : yAxisName
    }
  });

/**
 * Create a Y-Axis container  
 * @param {int} maximumField Maximum number of fields in the container
 * @param {string} yAxisNameKey The locale key of yAxis name. Default value is 'Value'.
 */
export const createYAxisSchema = (maximumField, yAxisNameKey = YAXIS_VALUE_KEY) =>
  createYAxisCustomNameSchema(
    maximumField,
    String.format(localizer.getText(REPORTDESIGNER_LABEL_Y_AXIS_NAME_KEY), localizer.getText(yAxisNameKey)));

/**
 * Create a Y-Axis container for transposed chart (labeled as "X-axis")
 * @param {int} maximumField Maximum number of fields in the container
 * @param {string} yAxisNameKey The locale key of yAxis name. Default value is 'Value'.
 */
export const createYAxisSchemaWithTransposedLabel = (maximumField, yAxisNameKey = XAXIS_VALUE_KEY) =>
  createYAxisCustomNameSchema(maximumField, 
    String.format(localizer.getText(REPORTDESIGNER_LABEL_X_AXIS_NAME_KEY), localizer.getText(yAxisNameKey)));

/**
 * Create Y-Axis Metrics container
 */
export const createYAxisMetricsSchema = () => ({ chart_yaxisMetrics: true });

/**
 * Create Y-Axis Gauge Metrics container
 */
export const createGaugeYAxisMetricsSchema = () => ({ gauge_yaxisMetrics: true });

/**
 * Create Value Labels container
 * @param {int} maximumField Maximum number of fields in the container
 */
export const createValuesLabelsSchema = maximumField => () => ({ chart_valuesLabels: { maximumField } });

/**
 * Create Bubble Size container
 * @param {int} maximumField Maximum number of fields in the container
 */
export const createBubbleSizeSchema = maximumField => () => ({ chart_bubbleSize: { maximumField } });

/**
 * Create Total Label container
 */
export const createTotalLabelSchema = () => ({ chart_totalLabel: true });

/**
 * Create Separator container
 * @param {Object} reportPartContent
 */
export const createSeparatorSchema = reportPartContent =>
  getOr(false, USE_SEPARATOR_PROP_PATH, reportPartContent) ? { chart_separators: { maximumField: 1 } } : {};

/**
 * Create a static label
 * @param {string} key The unique container key
 * @param {string|Function} label The label text value or a function return a text
 */
export const createStaticLabelSchema = (key, label) => () => ({
  staticLabel: {
    containerKey: key,
    get label() {
      return isFunction(label) ? label() : label;
    }
  }
});



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/FieldContainers/Izenda.FieldContainerSchemaFunctions.js